// src/components/shared/Navigation.jsx — Story Threads Vibe.
//
// Menu order (Nigel, 2026-09-10): Assessment, Dive In, Toolkit, Resources,
// Login, all present now. Dashboard
// sits in the menu too (Nigel, 2026-09-24), as it does in MindEase Vibe and
// Friendships Vibe. There is no Home item (the logo goes home), and the "Try Mode"
// badge and "Get Full Access" button are gone. The dashboard is in the
// signed-in menu, and the Dive In pages link to it.
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen,
  ClipboardList,
  LifeBuoy,
  Lock,
  LogOut,
  Menu,
  TrendingUp,
  User,
  Wrench,
  X,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { usePrivacyAuth } from '../../context/PrivacyAuthContext';
import { useGamification } from '../../context/VibeGamificationContext';
import { trackScreen } from '../../services/analytics';

const NAV_ITEMS = [
  { id: 'assessment-intro', label: 'Assessment', icon: ClipboardList },
  { id: 'modules', label: 'Dive In', icon: BookOpen },
  { id: 'toolkit', label: 'Toolkit', icon: Wrench },
  { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
  { id: 'resources', label: 'Resources', icon: LifeBuoy },
];

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { currentView, setCurrentView } = useApp();
  const { user, logout } = usePrivacyAuth();
  const { currentLevel, totalXP, currentLevelData } = useGamification() || {};

  const isActive = (id) => {
    if (id === 'modules') return currentView === 'modules' || currentView === 'story-threads-advanced-addon';
    if (id === 'assessment-intro') return currentView.startsWith('assessment');
    return currentView === id;
  };

  const go = (viewId) => {
    trackScreen(viewId);
    setCurrentView(viewId);
    // A page already showing this view gets no re-render from setCurrentView,
    // so the modules and toolkit pages listen for this and close whatever they
    // had open. Without it, "Toolkit" does nothing while a tool is open.
    window.dispatchEvent(new CustomEvent('story-threads:navigate', { detail: viewId }));
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  };

  const handleLogout = () => {
    logout(true);
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
  };

  const goToLogin = () => {
    window.location.href = '/login.html';
  };

  const itemClass = (id) =>
    `min-h-[44px] px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm whitespace-nowrap ${
      isActive(id) ? 'bg-thread-ember text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-thread-steel/50'
    }`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-thread-dark/95 backdrop-blur-sm border-b border-thread-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-28">
          <button
            type="button"
            onClick={() => go('home')}
            className="flex items-center gap-3 text-left"
          >
            <img src="/story-threads-vibe-logo-96.webp" alt="" width="48" height="48" className="w-12 h-12 rounded-xl" />
            <span className="flex flex-col">
              <span className="font-bold text-xl text-white leading-none font-display whitespace-nowrap">Story Threads</span>
              <span className="text-sm text-thread-sand font-normal leading-none mt-1">Vibe Edition</span>
            </span>
            {/* The name comes from the visible text, so voice control users can
                say what they see; this only adds where the button goes. */}
            <span className="sr-only">, home</span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item.id)}
                aria-current={isActive(item.id) ? 'page' : undefined}
                className={itemClass(item.id)}
              >
                <item.icon className="w-4 h-4" aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            ))}
            {!user && (
              <button type="button" onClick={goToLogin} className={itemClass('login')}>
                <User className="w-4 h-4" aria-hidden="true" />
                <span>Login</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden md:flex items-center gap-3">
                {currentLevel && (
                  <div className="flex items-center gap-2 bg-thread-darker/60 rounded-lg px-3 py-1.5">
                    <span className="text-sm text-slate-400">Level</span>
                    <span className="text-sm font-bold text-white">{currentLevel}</span>
                    <span className="text-xs" aria-hidden="true">{currentLevelData?.emoji || '🌱'}</span>
                  </div>
                )}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    aria-expanded={showUserMenu}
                    className="flex items-center gap-2 bg-thread-darker/60 hover:bg-thread-steel/50 rounded-lg min-h-[44px] px-3 py-1.5 transition-colors"
                  >
                    <User className="w-4 h-4 text-slate-300" aria-hidden="true" />
                    <span className="text-sm text-white font-medium">{user.username || user.email || 'Profile'}</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-xl border border-slate-700 py-1">
                      <button
                        type="button"
                        onClick={() => go('data-privacy')}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700"
                      >
                        <Lock className="w-4 h-4" aria-hidden="true" />
                        Privacy &amp; Data
                      </button>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700"
                      >
                        <LogOut className="w-4 h-4" aria-hidden="true" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              className="md:hidden p-3 text-slate-300 hover:text-white rounded-lg hover:bg-slate-800/50 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden border-t border-thread-border py-4"
          >
            <div className="flex flex-col gap-2">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => go(item.id)}
                  aria-current={isActive(item.id) ? 'page' : undefined}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors w-full text-left whitespace-nowrap ${
                    isActive(item.id) ? 'bg-thread-ember text-white' : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <item.icon className="w-5 h-5" aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              ))}

              {user ? (
                <div className="border-t border-thread-border pt-4 mt-2 space-y-2">
                  {currentLevel && (
                    <div className="flex items-center justify-between px-4 py-2 bg-slate-800/30 rounded-lg">
                      <span className="text-sm text-slate-400">
                        Level {currentLevel} <span aria-hidden="true">{currentLevelData?.emoji || '🌱'}</span>
                      </span>
                      <span className="text-xs text-slate-400">{totalXP || 0} XP</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => go('data-privacy')}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/50 w-full text-left"
                  >
                    <Lock className="w-5 h-5" aria-hidden="true" />
                    <span>Privacy &amp; Data</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:text-white hover:bg-red-600/20 w-full text-left"
                  >
                    <LogOut className="w-5 h-5" aria-hidden="true" />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={goToLogin}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-300 hover:text-white hover:bg-slate-800/50 w-full text-left"
                >
                  <User className="w-5 h-5" aria-hidden="true" />
                  <span>Login</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
